import { memo, useCallback, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ScheduleApplicationStatus } from "@common/types/domains/accounting/schedule";
import { TransactionColumn } from "@common/types/views/transactionViews";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { getTransactionsTableMeta } from "@hooks/view/useViewTableMeta";
import { AlertCircleIcon, CheckIcon } from "@icons";
import {
    useConfirmScheduleDraftsMutation,
    useDismissScheduleDraftsMutation,
    useDismissSuspiciousTransactionsMutation,
    useRestoreScheduleDraftsMutation,
    useRestoreSuspiciousTransactionsMutation,
} from "@api/scheduleReview.api";
import accrualStyles from "@components/accounting/accrualReview/AccrualReviewPage.module.scss";
import { useScheduleReviewOutcome } from "./ScheduleReviewOutcome.context";
import { useScheduleReviewDismiss } from "./useScheduleReviewDismiss";
import styles from "./ScheduleReviewActionCell.module.scss";

/**
 * Row action for the schedule review tasks. A rule-suggested draft offers
 * Confirm / Dismiss; an unscheduled suspicious transaction offers Set
 * schedule (opens the row's Schedule picker) / Dismiss; a row whose schedule
 * just became active shows a check until the list refetches. A draft that
 * failed to activate keeps its actions and shows why it failed.
 */
export const ScheduleReviewActionCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, TransactionListItem, unknown>) => {
        const transaction = row.original;
        const schedule = transaction.schedule;
        const entityId = transaction.entity.id;
        const meta = getTransactionsTableMeta(table);
        const { refuseIfViewOnly } = useViewOnlyRefusal();
        const { toast, error } = useToaster();
        const [isConfirming, setIsConfirming] = useState(false);
        const {
            failureMessagesByApplicationId,
            recordFailures,
            clearFailures,
        } = useScheduleReviewOutcome();
        const failureMessage = schedule
            ? failureMessagesByApplicationId.get(schedule.scheduleApplicationId)
            : undefined;

        const confirmDrafts = useConfirmScheduleDraftsMutation();
        const dismissDraftsMutation = useDismissScheduleDraftsMutation();
        const restoreDraftsMutation = useRestoreScheduleDraftsMutation();
        const dismissSuspiciousMutation =
            useDismissSuspiciousTransactionsMutation();
        const restoreSuspiciousMutation =
            useRestoreSuspiciousTransactionsMutation();

        const dismissDraft = useScheduleReviewDismiss<string>({
            dismiss: (applicationIds) =>
                dismissDraftsMutation.mutateAsync({ entityId, applicationIds }),
            restore: (applicationIds) =>
                restoreDraftsMutation.mutateAsync({ entityId, applicationIds }),
            noun: "schedule suggestion",
        });
        const dismissTransaction = useScheduleReviewDismiss<number>({
            dismiss: (transactionIds) =>
                dismissSuspiciousMutation.mutateAsync({
                    entityId,
                    transactionIds,
                }),
            restore: (transactionIds) =>
                restoreSuspiciousMutation.mutateAsync({
                    entityId,
                    transactionIds,
                }),
            noun: "transaction",
        });

        const handleConfirmDraft = useCallback(async () => {
            if (!schedule || refuseIfViewOnly()) {
                return;
            }
            setIsConfirming(true);
            try {
                const result = await confirmDrafts.mutateAsync({
                    entityId,
                    applicationIds: [schedule.scheduleApplicationId],
                });
                if (result.failed.length > 0) {
                    recordFailures(result.failed);
                    error(result.failed[0].message);
                    return;
                }
                clearFailures([schedule.scheduleApplicationId]);
                toast(`${schedule.scheduleName} activated`);
            } catch (confirmError) {
                error(
                    confirmError instanceof Error
                        ? confirmError.message
                        : "Failed to confirm schedule",
                );
            } finally {
                setIsConfirming(false);
            }
        }, [
            clearFailures,
            confirmDrafts,
            entityId,
            error,
            recordFailures,
            refuseIfViewOnly,
            schedule,
            toast,
        ]);

        const handleSetSchedule = useCallback(() => {
            if (refuseIfViewOnly()) {
                return;
            }
            const didOpen = meta.openCellAction?.({
                columnId: TransactionColumn.SCHEDULE,
                rowId: transaction.id,
            });
            if (!didOpen) {
                error("Show the Schedule column to set a schedule");
            }
        }, [error, meta, refuseIfViewOnly, transaction.id]);

        const handleDismiss = useCallback(() => {
            if (refuseIfViewOnly()) {
                return;
            }
            if (schedule?.status === ScheduleApplicationStatus.draft) {
                clearFailures([schedule.scheduleApplicationId]);
                void dismissDraft([schedule.scheduleApplicationId]);
                return;
            }
            void dismissTransaction([transaction.id]);
        }, [
            clearFailures,
            dismissDraft,
            dismissTransaction,
            refuseIfViewOnly,
            schedule,
            transaction.id,
        ]);

        if (schedule && schedule.status !== ScheduleApplicationStatus.draft) {
            return (
                <ViewTableCell className={accrualStyles.centeredCell}>
                    <span
                        className={accrualStyles.acceptedAction}
                        aria-label="Schedule applied"
                    >
                        <CheckIcon />
                    </span>
                </ViewTableCell>
            );
        }

        const isDraft = schedule?.status === ScheduleApplicationStatus.draft;

        return (
            <ViewTableCell className={accrualStyles.centeredCell}>
                <div className={accrualStyles.actionButtons}>
                    {failureMessage && (
                        <Tooltip
                            title="Couldn't activate schedule"
                            content={failureMessage}
                        >
                            <span
                                className={styles.failedIndicator}
                                role="img"
                                aria-label={`Couldn't activate schedule: ${failureMessage}`}
                            >
                                <AlertCircleIcon aria-hidden />
                            </span>
                        </Tooltip>
                    )}
                    <Button
                        variant="primary"
                        size="md"
                        onClick={
                            isDraft
                                ? () => void handleConfirmDraft()
                                : handleSetSchedule
                        }
                        disabled={isConfirming}
                        className={viewTableStyles.showOnHover}
                    >
                        {isDraft ? "Confirm" : "Set recognition schedule"}
                    </Button>
                    <Menu.More
                        size="md"
                        className={viewTableStyles.showOnHover}
                    >
                        <Menu.Item onClick={handleDismiss}>Dismiss</Menu.Item>
                    </Menu.More>
                </div>
            </ViewTableCell>
        );
    },
);
ScheduleReviewActionCell.displayName = "ScheduleReviewActionCell";
